import { useRoomContext } from "@livekit/components-react";
import type { Room } from "livekit-client";
import { ConnectionState, RoomEvent } from "livekit-client";
import { useEffect, useRef } from "react";

const JOIN_SOUND_URL = `${import.meta.env.BASE_URL}notifications/channels/join.mp3`;
const LEAVE_SOUND_URL = `${import.meta.env.BASE_URL}notifications/channels/leave.mp3`;
const SOUND_VOLUME = 0.3;
const INIT_DELAY_MS = 1000;

const roomsWithJoinSoundPlayed = new WeakSet<object>();
const roomsWithLeaveListenerAttached = new WeakSet<object>();

function noop() {
	// Intentionally empty, used as a catch handler.
}

let leaveAudio: HTMLAudioElement | null = null;
function getLeaveAudio() {
	if (typeof window === "undefined") return null;
	if (!leaveAudio) {
		leaveAudio = new Audio(LEAVE_SOUND_URL);
		leaveAudio.volume = SOUND_VOLUME;
	}
	return leaveAudio;
}

function attachLeaveListener(room: Room) {
	if (roomsWithLeaveListenerAttached.has(room)) return;
	roomsWithLeaveListenerAttached.add(room);

	const handleDisconnected = () => {
		getLeaveAudio()?.play().catch(noop);
		room.off(RoomEvent.Disconnected, handleDisconnected);
	};
	room.on(RoomEvent.Disconnected, handleDisconnected);
}

export function useParticipantSounds() {
	const room = useRoomContext();
	const joinAudioRef = useRef<HTMLAudioElement | null>(null);
	const leaveAudioRef = useRef<HTMLAudioElement | null>(null);
	const hasInitializedRef = useRef(false);

	useEffect(() => {
		if (typeof window === "undefined") return;

		joinAudioRef.current = new Audio(JOIN_SOUND_URL);
		joinAudioRef.current.volume = SOUND_VOLUME;
		leaveAudioRef.current = new Audio(LEAVE_SOUND_URL);
		leaveAudioRef.current.volume = SOUND_VOLUME;

		return () => {
			joinAudioRef.current = null;
			leaveAudioRef.current = null;
		};
	}, []);

	useEffect(() => {
		if (!room) return;

		const handleParticipantConnected = () => {
			if (!hasInitializedRef.current) return;
			joinAudioRef.current?.play().catch(noop);
		};

		const handleParticipantDisconnected = () => {
			if (!hasInitializedRef.current) return;
			leaveAudioRef.current?.play().catch(noop);
		};

		const handleLocalConnected = () => {
			if (!roomsWithJoinSoundPlayed.has(room)) {
				joinAudioRef.current?.play().catch(noop);
				roomsWithJoinSoundPlayed.add(room);
			}
			hasInitializedRef.current = true;
		};

		if (
			room.state === ConnectionState.Connected &&
			!roomsWithJoinSoundPlayed.has(room)
		) {
			joinAudioRef.current?.play().catch(noop);
			roomsWithJoinSoundPlayed.add(room);
			hasInitializedRef.current = true;
		}

		attachLeaveListener(room);

		const initTimeout = setTimeout(() => {
			hasInitializedRef.current = true;
		}, INIT_DELAY_MS);

		room.on(RoomEvent.Connected, handleLocalConnected);
		room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
		room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);

		return () => {
			clearTimeout(initTimeout);
			hasInitializedRef.current = false;
			room.off(RoomEvent.Connected, handleLocalConnected);
			room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
			room.off(
				RoomEvent.ParticipantDisconnected,
				handleParticipantDisconnected
			);
		};
	}, [room]);
}
